import React, { useState, useRef, useEffect } from 'react';
import { patchOrganizer, deleteOrganizer, readOrganizer } from './Profile';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FaPencilAlt, FaSave } from 'react-icons/fa'; // <-- Add this

const OrgProfileSection = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await readOrganizer();
        if (!response) return;
        setProfileData(response);
        setFullName(response.full_name || '');
        setEmail(response.email || '');
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  // Edit state for fields
  const [editingField, setEditingField] = useState(null);
  const [pendingFullName, setPendingFullName] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [savingField, setSavingField] = useState(null);

  // Manual save handler
  const handleSave = async (field) => {
    setSavingField(field);
    try {
      const formData = new FormData();
      if (field === 'full_name') {
        formData.append('data', JSON.stringify({ full_name: pendingFullName }));
      } else if (field === 'email') {
        formData.append('data', JSON.stringify({ email: pendingEmail }));
      }
      await patchOrganizer(formData);
      const updated = await readOrganizer();
      setProfileData(updated);
      setFullName(updated.full_name || '');
      setEmail(updated.email || '');
      setEditingField(null);
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSavingField(null);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return alert('Invalid image file.');
    setUploadingImage(true);

    try {
      const options = { maxSizeMB: 0.01, maxWidthOrHeight: 1024, fileType: 'image/jpeg', initialQuality: 0.3 };
      const compressedFile = await imageCompression(file, options);
      const finalFile = new File([compressedFile], 'profile.jpg', { type: 'image/jpeg' });
      setProfileImage(finalFile);

      const formData = new FormData();
      formData.append('data', JSON.stringify({ full_name: fullName, email }));
      formData.append('profile_image', finalFile);

      await patchOrganizer(formData);
      const updated = await readOrganizer();
      setProfileData(updated);
    } catch (err) {
      alert('Image upload failed: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenType');
    setLoggingOut(true);
    setTimeout(() => {
      navigate('/');
    }, 10);
  };

  const confirmDeleteAccount = async () => {
    try {
      const success = await deleteOrganizer();
      if (success) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('tokenType');
        setShowDeleteModal(false);
        navigate('/');
      }
    } catch {
      alert('Delete failed.');
    }
  };

  return (
    <div className="w-100 h-100 d-flex flex-column position-relative">

      <h5 className="text-center mb-4">Profile</h5>

      {/* Profile Image */}
      <div className="position-relative d-flex justify-content-center mb-4">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: '120px', height: '120px', backgroundColor: '#f0f0f0', overflow: 'hidden', cursor: 'pointer' }}
          onClick={() => fileInputRef.current?.click()}
        >
          {profileImage ? (
            <img
              src={URL.createObjectURL(profileImage)}
              alt="Profile"
              className="rounded-circle w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          ) : profileData?.profile_image ? (
            <img
              src={`${profileData.profile_image}?t=${Date.now()}`}
              alt="Profile"
              className="rounded-circle w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <img
              src="/images/avatar.jpg"
              alt="Profile Fallback"
              className="rounded-circle w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          )}

          {uploadingImage && (
            <div className="position-absolute top-50 start-50 translate-middle">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
        </div>
      </div>

      <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImageUpload} />

      {/* Full Name */}
      <div className="mb-3">
        <label className="form-label">Full Name</label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            value={editingField === 'full_name' ? pendingFullName : fullName}
            disabled={editingField !== 'full_name'}
            onChange={(e) => setPendingFullName(e.target.value)}
          />
          <span className="input-group-text bg-white border-0">
            {editingField === 'full_name' ? (
              <Button
                variant="outline-success"
                size="sm"
                disabled={savingField === 'full_name'}
                onClick={() => handleSave('full_name')}
              >
                {savingField === 'full_name' ? <Spinner size="sm" /> : <FaSave />}
              </Button>
            ) : (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setEditingField('full_name');
                  setPendingFullName(fullName);
                }}
              >
                <FaPencilAlt />
              </Button>
            )}
          </span>
        </div>
      </div>

      {/* Email */}
      <div className="mb-4">
        <label className="form-label">Email</label>
        <div className="input-group">
          <input
            type="email"
            className="form-control"
            value={editingField === 'email' ? pendingEmail : email}
            disabled={editingField !== 'email'}
            onChange={(e) => setPendingEmail(e.target.value)}
          />
          <span className="input-group-text bg-white border-0">
            {editingField === 'email' ? (
              <Button
                variant="outline-success"
                size="sm"
                disabled={savingField === 'email'}
                onClick={() => handleSave('email')}
              >
                {savingField === 'email' ? <Spinner size="sm" /> : <FaSave />}
              </Button>
            ) : (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setEditingField('email');
                  setPendingEmail(email);
                }}
              >
                <FaPencilAlt />
              </Button>
            )}
          </span>
        </div>
      </div>

      {/* Actions below fields */}
      <div className="d-grid gap-2 mt-3">
        <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete Account</Button>
        <Button variant="secondary" onClick={handleLogout}>Logout</Button>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete your account? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDeleteAccount}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* Logout Spinner Overlay */}
      {loggingOut && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}>
          <Spinner animation="border" variant="light" style={{ width: '4rem', height: '4rem' }} />
        </div>
      )}
    </div>
  );
};

export default OrgProfileSection;
